import configs from 'config/environment'
import { getUserId } from 'lib/auth/tokenStorage'
import {
  sendReduxActionLogs,
  sendReduxActionLogsBeacon
} from 'api/useReduxActionLogs'

// ---------------------------------------------------------------------------
// What to skip
//
// Action types matching ANY of these patterns are dropped before they ever
// reach the buffer. Reasons:
//   persist/*, @@*       framework noise, no debugging value
//   loader/*             dispatched on every API call, extremely high freq
//   idleTime/*           dispatched on a timer, extremely high freq
//   apiCall/*            internal book-keeping, no debugging value
//   *password|token|...  may carry credentials in their payload
// ---------------------------------------------------------------------------
const IGNORED_ACTION_PATTERNS = [
  /^persist\//,
  /^@@/,
  /^loader\//i,
  /^idleTime\//i,
  /^apiCall\//i,
  /password/i,
  /token/i,
  /secret/i,
  /\botp\b/i,
  /^idle_time\/setIdleTimeTotal$/i
]

// Keys whose values are replaced with '[REDACTED]' anywhere they appear in a
// payload (recursive, case-insensitive). Belt-and-braces alongside the action
// filter above — even allowed actions can carry sensitive sub-fields.
const SENSITIVE_KEY_PATTERNS = [
  /password/i,
  /passwd/i,
  /\bpwd\b/i,
  /token/i,
  /authorization/i,
  /\bauth\b/i,
  /secret/i,
  /api[_-]?key/i,
  /\bcvv\b/i,
  /\bcvc\b/i,
  /card[_-]?number/i,
  /\bssn\b/i,
  /\bpin\b/i,
  /otp/i
]

const MAX_STRING_LEN = 500
const MAX_PAYLOAD_BYTES = 10 * 1024
const MAX_DEPTH = 5
const MAX_ARRAY_LEN = 50
const MAX_BUFFER_LEN = 50
const FLUSH_INTERVAL_MS = 5000

let buffer = []
let flushTimer = null
let listenersAttached = false

const matchesAny = (str, patterns) => {
  if (typeof str !== 'string') return false
  for (let i = 0; i < patterns.length; i++) {
    if (patterns[i].test(str)) return true
  }
  return false
}

const shouldIgnoreAction = action => {
  if (!action || typeof action.type !== 'string') return true
  return matchesAny(action.type, IGNORED_ACTION_PATTERNS)
}

const isUnloggableValue = value => {
  if (value == null) return false
  if (typeof File !== 'undefined' && value instanceof File) return true
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true
  if (typeof FormData !== 'undefined' && value instanceof FormData) return true
  if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) {
    return true
  }
  return false
}

// Crude but cheap detector for a base64-encoded blob (PDFs, images, etc.).
// We never want to ship those — they bloat the payload and the original file
// is already on the server anyway.
const looksLikeBase64Blob = str =>
  typeof str === 'string' &&
  str.length > 1000 &&
  /^(data:[^;]+;base64,)?[A-Za-z0-9+/=\s]+$/.test(str)

const sanitize = (value, depth) => {
  if (depth > MAX_DEPTH) return '[redacted: depth-limit]'
  if (value == null) return value

  if (isUnloggableValue(value)) return '[redacted: file/blob]'

  const t = typeof value
  if (t === 'string') {
    if (looksLikeBase64Blob(value)) return '[redacted: base64-blob]'
    if (value.length > MAX_STRING_LEN) {
      return value.slice(0, MAX_STRING_LEN) + '…[truncated]'
    }
    return value
  }
  if (t === 'number' || t === 'boolean') return value
  if (t === 'function' || t === 'symbol') return `[${t}]`

  if (Array.isArray(value)) {
    const out = []
    const len = Math.min(value.length, MAX_ARRAY_LEN)
    for (let i = 0; i < len; i++) out.push(sanitize(value[i], depth + 1))
    if (value.length > MAX_ARRAY_LEN) {
      out.push(`…[+${value.length - MAX_ARRAY_LEN} more]`)
    }
    return out
  }

  if (t === 'object') {
    const out = {}
    for (const key of Object.keys(value)) {
      if (matchesAny(key, SENSITIVE_KEY_PATTERNS)) {
        out[key] = '[REDACTED]'
        continue
      }
      out[key] = sanitize(value[key], depth + 1)
    }
    return out
  }

  return value
}

const safeSerialize = obj => {
  try {
    return JSON.stringify(obj)
  } catch {
    return null
  }
}

const isOnLoginRoute = () =>
  typeof window !== 'undefined' && window.location.pathname === '/login'

const shouldSkipForAuthFlow = state =>
  isOnLoginRoute() || state?.auth?.authCard === 'verify-code'

const flush = () => {
  if (buffer.length === 0) return
  if (isOnLoginRoute()) return
  const batch = buffer
  buffer = []
  try {
    sendReduxActionLogs(batch)
  } catch {
    // best-effort
  }
}

const flushBeacon = () => {
  if (buffer.length === 0) return
  const batch = buffer
  buffer = []
  try {
    sendReduxActionLogsBeacon(batch)
  } catch {
    // best-effort
  }
}

const ensureFlushScheduled = () => {
  if (flushTimer != null) return
  flushTimer = setInterval(() => {
    try {
      flush()
    } catch {
      // best-effort
    }
  }, FLUSH_INTERVAL_MS)
}

const ensureUnloadListeners = () => {
  if (listenersAttached) return
  if (typeof window === 'undefined') return
  // pagehide is the recommended modern signal; beforeunload kept as a fallback
  // for older browsers and Firefox edge cases.
  window.addEventListener('pagehide', flushBeacon)
  window.addEventListener('beforeunload', flushBeacon)
  // Also flush when the tab is hidden so we don't lose buffered logs on
  // mobile where unload events aren't always fired.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushBeacon()
  })
  listenersAttached = true
}

const resolveUserId = state =>
  state?.auth?.data?.user?.id ??
  state?.auth?.data?.id ??
  state?.user?.id ??
  state?.user?.current_user?.id ??
  (typeof localStorage !== 'undefined' ? getUserId() : null) ??
  null

export const reduxLoggerMiddleware = store => next => action => {
  // Always advance the action FIRST. Anything below is best-effort and must
  // not be allowed to stall or break the dispatch chain.
  const result = next(action)

  if (!configs.reduxLogEnabled) return result

  try {
    if (shouldIgnoreAction(action)) return result

    const state = store.getState()
    if (shouldSkipForAuthFlow(state)) return result

    const sanitizedPayload = sanitize(action.payload, 0)
    const serialized = safeSerialize(sanitizedPayload)

    // Skip absurdly large payloads even after sanitization — these are almost
    // always file/state dumps that aren't useful for debugging.
    if (serialized && serialized.length > MAX_PAYLOAD_BYTES) return result

    buffer.push({
      type: action.type,
      payload: sanitizedPayload,
      meta: action.meta ?? null,
      // userId: resolveUserId(state),
      url: typeof window !== 'undefined' ? window.location.pathname : null,
      timestamp: new Date().toISOString()
    })

    ensureFlushScheduled()
    ensureUnloadListeners()

    if (buffer.length >= MAX_BUFFER_LEN) flush()
  } catch {
    // Logging itself must never crash the app.
  }

  return result
}

// Exported for tests / manual triggers from a console if needed.
export const __reduxLoggerInternals = {
  flush,
  flushBeacon,
  getBufferLength: () => buffer.length
}
